declare const __PRIMARY_ORIGIN__: string;

const REDIRECT_GUARD_KEY = "easyorders.primary-redirect-at";
const GUARD_WINDOW_MS = 60_000;
const LEGACY_PRIMARY_ORIGIN = "https://91-99-111-239.sslip.io";

// Browser storage cannot move between origins. Keep unsent work accessible at its original
// address, and fail closed if storage cannot be inspected. The recovery link is explicit.
function keepLegacyOrigin(): boolean {
  if (window.location.origin !== LEGACY_PRIMARY_ORIGIN) return false;
  if (new URLSearchParams(window.location.search).get("legacy") === "1") return true;
  try {
    return Object.keys(window.localStorage)
      .filter((key) => /^easyorders\.pwa\..+\.(?:draft\..+|pending)$/.test(key))
      .some((key) => {
        const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "null");
        return !Array.isArray(value) || value.length > 0;
      });
  } catch {
    return true;
  }
}

/**
 * Moves the app onto the primary deployment when it is running anywhere else. Resolves true when
 * this page is being replaced by the primary, false when it stays.
 *
 * The API client calls relative paths, so a PWA installed from the standby keeps talking to the
 * standby for good. That looks healthy — it syncs, it accepts orders — but only the primary holds
 * the tunnel to the shop, so the standby quietly queues every ticket for an on-site relay that is
 * logged into the primary and will never see them. The print then waits forever.
 *
 * Staying put when the primary is unreachable is deliberate: that is the whole point of a standby.
 */
export async function preferPrimaryOrigin(): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }
  const primary = (__PRIMARY_ORIGIN__ ?? "").trim().replace(/\/+$/, "");
  if (!primary || window.location.origin === primary) {
    return false;
  }
  if (keepLegacyOrigin()) {
    return false;
  }
  // A dev server is not a deployment that lost its way; sending it to production would make
  // `npm run dev` impossible to use.
  if (["localhost", "127.0.0.1", "::1", "0.0.0.0"].includes(window.location.hostname)) {
    return false;
  }

  // One hop is a fix, two is a loop. If the primary ever serves a build that wants to redirect
  // again, stop rather than bounce the user between deployments.
  const last = Number(window.sessionStorage.getItem(REDIRECT_GUARD_KEY) ?? "0");
  if (Number.isFinite(last) && last > 0 && Date.now() - last < GUARD_WINDOW_MS) {
    return false;
  }

  try {
    // Reachable is not the same as healthy: an opaque no-cors response resolves even for a 502,
    // which would move everyone off a working standby onto a broken primary. /api/health sends
    // Access-Control-Allow-Origin so the status can actually be read.
    const response = await fetch(`${primary}/api/health`, { cache: "no-store" });
    if (!response.ok) {
      return false;
    }
    const health = await response.json();
    if (!health?.ok) {
      return false;
    }
  } catch {
    return false;
  }

  // A draft or queued submission could have appeared while the health request was in flight.
  if (keepLegacyOrigin()) return false;
  window.sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  window.location.replace(primary + window.location.pathname + window.location.search);
  return true;
}
